/** @typedef {import('./actions').GetActionMap} GetActionMap */

import { createContext, useContext } from 'react';

type ZipFileEntry = { fileId: string; path: string };

/**
 * The file manager context interface.
 */
interface IFileManagerContext {
  uploadFile: ({ fileName, contents }: { fileName: string; contents: string | ArrayBuffer | null }) => Promise<string>;
  readFile: ({ fileId }: { fileId: string }) => Promise<Blob>;
  getFileMetadata: ({ fileId }: { fileId: string }) => Promise<any>;
  getFileThumbnail: ({ fileId, size }: { fileId: string; size: string }) => Promise<Blob>;
  createTimeAccessToken: ({ fileId, validForMinutes }: { fileId: string; validForMinutes: number }) => Promise<string>;
  buildTimeAccessFileUrl: ({ token, fileName }: { token: string; fileName: string }) => string;
  buildTimeAccessThumbnailUrl: ({
    token,
    fileName,
    thumbnailSize,
  }: {
    token: string;
    fileName: string;
    thumbnailSize: string;
  }) => string;
  initLargeFile: ({ fileName }: { fileName: string }) => Promise<string>;
  uploadLargeFile: ({ fileId, contents }: { fileId: string; contents: string | ArrayBuffer | null }) => Promise<void>;
  finalizeLargeFile: ({ fileId }: { fileId: string }) => Promise<void>;
  createEmptyZip: ({ fileName }: { fileName: string }) => Promise<string>;
  addZipFiles: ({ fileId, files }: { fileId: string; files: ZipFileEntry[] }) => Promise<void>;
  finalizeZip: ({ fileId }: { fileId: string }) => Promise<void>;
  buildZip: (params: {
    fileName: string;
    files: ZipFileEntry[];
    batchSize?: number;
    onProgress?: () => void;
  }) => Promise<string>;
  concatPdf: ({ fileName, fileIds }: { fileName: string; fileIds: string[] }) => Promise<string>;
}

/**
 * Creates empty context.
 */
export const FileManagerContext = createContext<IFileManagerContext>(undefined!);

/**
 * Requires the API context.
 */
export const useFileManager = () => useContext(FileManagerContext);
